"use client";

import { useState } from "react";
import { inputClass } from "@/components/form";
import { ImageTool, type ImagePlan } from "@/components/ImageTool";
import { Segmented } from "@/components/Segmented";
import { kindOf, outputName } from "@/lib/files";
import { OUTPUT_FORMATS, type OutputFormat } from "@/lib/image/format";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";

const tool = getTool("convert-image")!;

const BACKGROUNDS = [
  { value: "#ffffff", label: "White" },
  { value: "#000000", label: "Black" },
];

type Settings = { format: OutputFormat; background: string };

/** FR-20: JPG, PNG, WebP, AVIF, GIF (first frame), HEIC, BMP in; JPG, PNG, WebP out. */
export function planConvert(file: File, s: Settings): ImagePlan {
  const ext = OUTPUT_FORMATS[s.format].ext;
  return {
    job: { format: s.format, background: s.background },
    name: () => outputName(file.name, "converted", ext),
  };
}

export function ConvertTool() {
  const [s, setS] = useState<Settings>({
    format: "jpg",
    background: "#ffffff",
  });

  return (
    <ImageTool
      tool={tool}
      zipName="images-converted.zip"
      plan={(file) => planConvert(file, s)}
      actionLabel={(n) =>
        `Convert ${n > 1 ? `${n} images` : "image"} to ${OUTPUT_FORMATS[s.format].label}`
      }
      busyVerb="Converting"
      settings={({ file }) => (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold">Convert to</span>
            <Segmented
              label="Convert to"
              stretch
              value={s.format}
              onChange={(format) => setS((old) => ({ ...old, format }))}
              options={(["jpg", "png", "webp"] as const).map((f) => ({
                value: f,
                label: OUTPUT_FORMATS[f].label,
              }))}
            />
          </div>

          {s.format === "jpg" ? (
            <fieldset className="flex flex-col gap-2 rounded-lg bg-canvas p-3.5">
              <legend className="float-left mb-1 text-sm font-semibold">
                Background for see-through parts
              </legend>
              <p className="clear-both text-sm text-muted-foreground">
                JPG can&apos;t store transparency, so it is filled with this
                colour.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {BACKGROUNDS.map((b) => (
                  <button
                    key={b.value}
                    type="button"
                    aria-pressed={s.background === b.value}
                    onClick={() =>
                      setS((old) => ({ ...old, background: b.value }))
                    }
                    className={cn(
                      "inline-flex min-h-11 items-center gap-2 rounded-lg border-[1.5px] bg-card px-3 text-sm font-medium",
                      s.background === b.value
                        ? "border-brand"
                        : "border-line-strong",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="size-5 rounded-full border border-line-strong"
                      style={{ background: b.value }}
                    />
                    {b.label}
                  </button>
                ))}
                <label className="inline-flex min-h-11 items-center gap-2 text-sm font-medium">
                  Other
                  <input
                    type="color"
                    aria-label="Other background colour"
                    value={s.background}
                    onChange={(e) =>
                      setS((old) => ({ ...old, background: e.target.value }))
                    }
                    className={cn(inputClass, "min-h-11 w-14 p-1")}
                  />
                </label>
              </div>
            </fieldset>
          ) : (
            <p className="rounded-lg bg-canvas p-3.5 text-sm text-ink">
              {OUTPUT_FORMATS[s.format].label} keeps see-through parts as they
              are.
            </p>
          )}

          {file && kindOf(file) === "gif" && (
            <p className="text-sm text-ink">
              Animated GIFs: only the first frame is saved.
            </p>
          )}
        </>
      )}
    />
  );
}
